// Transport and editing tools: play the pattern or the whole song, record into the pattern with
// count-in, quantize, undo/redo, zoom, what the pads play, note repeat and MIDI.
import { useState } from "react";
import { FIRST_ATMOS, GRID_DIVISIONS, GRID_TICKS, laneSlot, PATTERN_NAMES, type GridDivision } from "@eb/shared";
import { quantizeLoop } from "./ops";
import { WizardButton } from "./WizardButton";
import { enableMidi } from "./padInput";
import { startPlay, startRecording, stopPlay } from "./audio";
import { useMedia } from "../ui/useMedia";
import { canRedoNow, canUndoNow, edit, redoEdit, undoEdit, useEditor } from "./store";

const TARGET_NAMES = ["Bass", "Harmony", "Lead", "Classics", "Atmos"];

/** What the pads can play: drums, each instrument, and the song's extra lanes ("Bass +"). */
function padTargets(lanes: number[] | undefined): [number, string, string][] {
  return [
    [0, "Drums", "Drums"],
    ...TARGET_NAMES.map((l, i): [number, string, string] => [16 + i, l, l]),
    ...(lanes ?? []).map((p): [number, string, string] => [p, `${TARGET_NAMES[laneSlot(p) - 16]} +`, `lane-${p}`]),
  ];
}

export function Transport() {
  const s = useEditor();
  const phone = useMedia("(max-width: 640px)");
  const [midi, setMidi] = useState<string>("");
  const playing = s.play;
  const btn = (mode: "loop" | "song", label: string, testId: string) => (
    <button
      type="button"
      className={`btn${playing === mode ? " btn-primary" : ""}`}
      aria-pressed={playing === mode}
      data-testid={testId}
      onClick={() => void (playing === mode ? stopPlay() : startPlay(mode))}
    >
      {playing === mode ? "■ Stop" : label}
    </button>
  );
  return (
    <section className="transport panel" aria-label="Transport">
      <div className="row">
        {btn("loop", `▶ Play pattern ${PATTERN_NAMES[s.pattern]}`, "play-loop")}
        {btn("song", "▶ Play song", "play-song")}
        <button
          type="button"
          className={`btn rec${playing === "recording" ? " on" : ""}`}
          aria-pressed={playing === "recording"}
          data-testid="record"
          onClick={() => void (playing === "recording" ? stopPlay() : startRecording())}
        >
          {playing === "recording" ? "■ Stop recording" : "● Record"}
        </button>
        <label className="toggle">
          <input type="checkbox" checked={s.countIn} onChange={(e) => s.set({ countIn: e.target.checked })} />
          <span>Count-in</span>
        </label>
        <WizardButton />
        {/* On phones the undo buttons stay in sight (the rest folds away). */}
        {phone && (
          <>
            <button
              type="button"
              className="btn btn-small"
              onClick={undoEdit}
              disabled={!canUndoNow()}
              data-testid="undo"
            >
              ↶ Undo
            </button>
            <button
              type="button"
              className="btn btn-small"
              onClick={redoEdit}
              disabled={!canRedoNow()}
              data-testid="redo"
            >
              ↷ Redo
            </button>
          </>
        )}
      </div>
      {phone ? (
        <details className="transport-more">
          <summary>More controls</summary>
          <div className="row">
            <label className="field">
              <span>Grid</span>
              <select
                value={s.grid}
                onChange={(e) => s.set({ grid: e.target.value as GridDivision })}
                data-testid="grid-select"
              >
                {GRID_DIVISIONS.map((g) => (
                  <option key={g}>{g}</option>
                ))}
              </select>
            </label>
            <label className="toggle">
              <input
                type="checkbox"
                checked={s.quantizeOnRecord}
                onChange={(e) => s.set({ quantizeOnRecord: e.target.checked })}
              />
              <span>Quantize recording</span>
            </label>
            <button
              type="button"
              className="btn btn-small"
              onClick={() => edit((l) => quantizeLoop(l, GRID_TICKS[s.grid], s.quantizeStrength))}
              data-testid="quantize"
            >
              Quantize all
            </button>
            <div className="segmented" role="radiogroup" aria-label="Zoom">
              {([4, 2, 1] as const).map((z) => (
                <button
                  key={z}
                  type="button"
                  role="radio"
                  aria-checked={s.zoomBars === z}
                  className={s.zoomBars === z ? "on" : ""}
                  onClick={() => s.set({ zoomBars: z, scrollBar: Math.min(s.scrollBar, 4 - z) })}
                >
                  {z === 1 ? "1 bar" : `${z} bars`}
                </button>
              ))}
            </div>
            {s.zoomBars < 4 && (
              <label className="field">
                <span>From bar</span>
                <input
                  type="range"
                  min={0}
                  max={4 - s.zoomBars}
                  value={s.scrollBar}
                  onChange={(e) => s.set({ scrollBar: Number(e.target.value) })}
                />
                <span className="mono">{s.scrollBar + 1}</span>
              </label>
            )}
          </div>
          <div className="row">
            <div className="segmented" role="radiogroup" aria-label="Pads play">
              {padTargets(s.hist.present.lanes).map(([v, l, id]) => (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={s.padTarget === v}
                  className={s.padTarget === v ? "on" : ""}
                  onClick={() => s.set({ padTarget: v })}
                  data-testid={`target-${id}`}
                >
                  {l}
                </button>
              ))}
            </div>
            {s.padTarget >= 16 && s.padTarget !== FIRST_ATMOS && (
              <span className="row">
                <button
                  type="button"
                  className="btn btn-small"
                  onClick={() => s.set({ padOctave: Math.max(-2, s.padOctave - 1) })}
                >
                  Oct −
                </button>
                <button
                  type="button"
                  className="btn btn-small"
                  onClick={() => s.set({ padOctave: Math.min(2, s.padOctave + 1) })}
                >
                  Oct +
                </button>
              </span>
            )}
            <label className="toggle">
              <input type="checkbox" checked={s.noteRepeat} onChange={(e) => s.set({ noteRepeat: e.target.checked })} />
              <span>Note repeat</span>
            </label>
            <select
              aria-label="Note repeat rate"
              value={s.repeatGrid}
              onChange={(e) => s.set({ repeatGrid: e.target.value as GridDivision })}
            >
              {GRID_DIVISIONS.map((g) => (
                <option key={g}>{g}</option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-small"
              onClick={async () => setMidi((await enableMidi()).ok ? "MIDI on" : "No MIDI")}
            >
              {midi || "Enable MIDI"}
            </button>
          </div>
        </details>
      ) : (
        <>
          <div className="row">
            <label className="field">
              <span>Grid</span>
              <select
                value={s.grid}
                onChange={(e) => s.set({ grid: e.target.value as GridDivision })}
                data-testid="grid-select"
              >
                {GRID_DIVISIONS.map((g) => (
                  <option key={g}>{g}</option>
                ))}
              </select>
            </label>
            <label className="toggle">
              <input
                type="checkbox"
                checked={s.quantizeOnRecord}
                onChange={(e) => s.set({ quantizeOnRecord: e.target.checked })}
              />
              <span>Quantize recording</span>
            </label>
            <button
              type="button"
              className="btn btn-small"
              onClick={() => edit((l) => quantizeLoop(l, GRID_TICKS[s.grid], s.quantizeStrength))}
              data-testid="quantize"
            >
              Quantize all
            </button>
            <button
              type="button"
              className="btn btn-small"
              onClick={undoEdit}
              disabled={!canUndoNow()}
              aria-keyshortcuts="Meta+Z Control+Z"
              data-testid="undo"
            >
              ↶ Undo
            </button>
            <button
              type="button"
              className="btn btn-small"
              onClick={redoEdit}
              disabled={!canRedoNow()}
              aria-keyshortcuts="Meta+Shift+Z Control+Shift+Z"
              data-testid="redo"
            >
              ↷ Redo
            </button>
            <div className="segmented" role="radiogroup" aria-label="Zoom">
              {([4, 2, 1] as const).map((z) => (
                <button
                  key={z}
                  type="button"
                  role="radio"
                  aria-checked={s.zoomBars === z}
                  className={s.zoomBars === z ? "on" : ""}
                  onClick={() => s.set({ zoomBars: z, scrollBar: Math.min(s.scrollBar, 4 - z) })}
                >
                  {z === 1 ? "1 bar" : `${z} bars`}
                </button>
              ))}
            </div>
            {s.zoomBars < 4 && (
              <label className="field">
                <span>From bar</span>
                <input
                  type="range"
                  min={0}
                  max={4 - s.zoomBars}
                  value={s.scrollBar}
                  onChange={(e) => s.set({ scrollBar: Number(e.target.value) })}
                />
                <span className="mono">{s.scrollBar + 1}</span>
              </label>
            )}
          </div>
          <div className="row">
            <div className="segmented" role="radiogroup" aria-label="Pads play">
              {padTargets(s.hist.present.lanes).map(([v, l, id]) => (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={s.padTarget === v}
                  className={s.padTarget === v ? "on" : ""}
                  onClick={() => s.set({ padTarget: v })}
                  data-testid={`target-${id}`}
                >
                  {l}
                </button>
              ))}
            </div>
            {s.padTarget >= 16 && s.padTarget !== FIRST_ATMOS && (
              <span className="row">
                <button
                  type="button"
                  className="btn btn-small"
                  onClick={() => s.set({ padOctave: Math.max(-2, s.padOctave - 1) })}
                >
                  Oct −
                </button>
                <button
                  type="button"
                  className="btn btn-small"
                  onClick={() => s.set({ padOctave: Math.min(2, s.padOctave + 1) })}
                >
                  Oct +
                </button>
              </span>
            )}
            <label className="toggle">
              <input type="checkbox" checked={s.noteRepeat} onChange={(e) => s.set({ noteRepeat: e.target.checked })} />
              <span>Note repeat</span>
            </label>
            <select
              aria-label="Note repeat rate"
              value={s.repeatGrid}
              onChange={(e) => s.set({ repeatGrid: e.target.value as GridDivision })}
            >
              {GRID_DIVISIONS.map((g) => (
                <option key={g}>{g}</option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-small"
              onClick={async () => setMidi((await enableMidi()).ok ? "MIDI on" : "No MIDI")}
            >
              {midi || "Enable MIDI"}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
